import { inject, Injectable, signal } from '@angular/core';
import { FolderNode } from '../../pages/protected/data-exchange/features/upload-files/folder-tree/folder-tree';
import JSZip from 'jszip';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthStore } from '../login/services/auth.store';
import { DataExchangeApiService } from '../data-exchange/data-exchange-api-service';
import { LoggerService } from '../logging/logger.service';


export const enum UploadTaskCreationStatus {
  NotStarted = 0,
  Pending = 1,
  Success = 2,
  Error = 3
}

/**
 * Interface representing the structure of an upload task, 
 * including details such as OEM, project, test name, development stage, test executers, and DUTs (Devices Under Test).
 */
export interface UploadTaskTemplateInputs {
  oem: string;
  project: string;
  testName: string;
  devStage: string;
  testExecuter: string[];
  dut: string[];
}

/**
 * Interface representing a folder node in the backend structure, which includes the folder name and optional children.
 * This structure is used to represent the folder hierarchy when creating an upload task.
 */
interface BackendFolderNode {
  name: string;
  children?: BackendFolderNode[];
}

/**
 * Interface representing the payload structure for creating an upload task,
 * which includes the template inputs, creator information, creation timestamp, and the folder structure.
 * This payload is sent to the backend when creating a new upload task.
 */
export interface UploadTaskPayload {
  templateInputs: UploadTaskTemplateInputs;
  createdBy: string | null;
  createdAt: string;
  folderStructure: BackendFolderNode[];
}


@Injectable({
  providedIn: 'root',
})
export class UploadTaskService {

  private readonly httpClient = inject(HttpClient);
  private readonly authStore = inject(AuthStore);
  private readonly dataExchangeApiService = inject(DataExchangeApiService);
  private readonly logger = inject(LoggerService);

  async createUploadTask(
    templateInputs: UploadTaskTemplateInputs,
    folderStructure: FolderNode[],
  ): Promise<UploadTaskCreationStatus> {
    this.logger.info('UploadTaskService', 'Starting upload task creation', { 
      dutCount: templateInputs.dut.length,
      rootCount: folderStructure.length,
    });
    
    // Prepare the payload to send to the backend.
    const payload: UploadTaskPayload = {
      templateInputs,
      createdBy: this.authStore.username(),
      createdAt: new Date().toISOString(),
      folderStructure: folderStructure.map(node => this.convertToBackendFolderNode(node)),
    };

    // Send the payload to the backend to create a new upload task.
    try {
      let response = await this.dataExchangeApiService.createUploadTask(payload);
      this.logger.info('UploadTaskService', 'Upload task created successfully');
      return UploadTaskCreationStatus.Success;
    } catch (error) {
      this.logger.error('UploadTaskService', 'Error creating upload task', error);
      return UploadTaskCreationStatus.Error;
    }
  }

  async createFolderStructure(
    file: File,
    inputs: UploadTaskTemplateInputs,
  ): Promise<FolderNode[]> {
    this.logger.debug('UploadTaskService', 'Creating folder structure from file', { fileSize: file.size, fileType: file.type });
    
    try {
      const testCaseNodes = await this.parseFile(file);
      const dutNodes: FolderNode[] = inputs.dut.map((dut, i) => ({
        id: `dut-${i}`,
        name: dut,
        expanded: false,
        visible: true,
        children: structuredClone(testCaseNodes),
      }));

      // Create the folder structure based on the template inputs and the parsed file structure.
      const tree: FolderNode[] = [{
        id: 'oem',
        name: inputs.oem,
        expanded: true,
        visible: true,
        children: [{
          id: 'project',
          name: inputs.project,
          expanded: false,
          visible: true,
          children: [{
            id: 'testname',
            name: inputs.testName,
            expanded: false,
            visible: true,
            children: [{
              id: 'devstage',
              name: inputs.devStage,
              expanded: false,
              visible: true,
              children: dutNodes,
            }],
          }],
        }],
      }];

      this.logger.debug('UploadTaskService', 'Folder structure created successfully', { nodeCount: tree.length });
      return tree;
    } catch (error) {
      this.logger.error('UploadTaskService', 'Error creating folder structure', error);
      throw error;
    }
  }

  createFolderStructureFromTestXplorer(
    testNameNodes: FolderNode[],
    inputs: UploadTaskTemplateInputs,
  ): FolderNode[] {
    this.logger.debug('UploadTaskService', 'Building TestXplorer folder structure', { testCount: testNameNodes.length });
    const testNamesWithDevelopmentStage = testNameNodes.map((testNameNode, index) => ({
      ...structuredClone(testNameNode),
      children: [{
        id: `devstage-${index}`,
        name: inputs.devStage,
        expanded: false,
        visible: true,
        children: structuredClone(testNameNode.children ?? []),
      }],
    }));

    return [{
      id: 'oem',
      name: inputs.oem,
      expanded: true,
      visible: true,
      children: [{
        id: 'project',
        name: inputs.project,
        expanded: false,
        visible: true,
        children: testNamesWithDevelopmentStage,
      }],
    }];
  }

  private convertToBackendFolderNode(node: FolderNode): BackendFolderNode {
    return {
      name: node.name,
      children: node.children?.map(child => this.convertToBackendFolderNode(child)),
    };
  }


  /**
   * Function to call the proper function to parse the uploaded file based on its type.
   * @param file - uploaded folderstructure from the user
   * @returns Promise resolving to an array of FolderNode representing the parsed folder structure.
   */
  private async parseFile(file: File): Promise<FolderNode[]> {
    if (file.name.endsWith('.zip')) {
      return this.parseZip(file);
    }
    // JSON: implementation for parsing JSON files can be added here in the future.
    this.logger.warn('UploadTaskService', 'Unsupported folder structure file type');
    return [];
  }

  /**
   * Parse zip file and return the folder structure as an array of FolderNode.
   * @param file - uploaded folderstructure from the user
   * @returns Promise resolving to an array of FolderNode representing the parsed folder structure.
   */
private async parseZip(file: File): Promise<FolderNode[]> {
  const zip = await JSZip.loadAsync(file);
  const root: FolderNode[] = [];

  // Get all folder entries in the zip file, sort them, and build the folder structure.
  const folders = Object.values(zip.files)
    .filter(entry => entry.dir)
    .map(entry => entry.name)
    .sort();

  // Insert each folder path into the root structure.
  for (const folder of folders) {
    const parts = folder.split('/').filter(Boolean);
    this.insertPath(root, parts, folder);
  }

  this.logger.debug('UploadTaskService', 'ZIP folder structure parsed', { folderCount: folders.length });
  return root;
}

  /**
   * Inserts a folder path into the folder structure.
   * @param nodes The current level of folder nodes.
   * @param parts The parts of the folder path to insert.
   * @param fullPath The full folder path.
   * @returns void
   */
  private insertPath(nodes: FolderNode[], parts: string[], fullPath: string): void {
    if (parts.length === 0) return;

    const [head, ...rest] = parts;
    let node = nodes.find(n => n.name === head);

    if (!node) {
      node = {
        id: fullPath,
        name: head,
        expanded: false,
        visible: true,
        children: rest.length > 0 ? [] : undefined,
      };
      nodes.push(node);
    }

    if (rest.length > 0) {
      node.children ??= [];
      this.insertPath(node.children, rest, fullPath);
    }
  }
}